export const PROJECT_STAGE_ORDER = [
  "content_generator",
  "scene_generator",
  "visual_planner",
  "audio_planner",
  "scene_planner",
  "animation_planner",
];

export const STAGE_LABELS: Record<string, string> = {
  content_generator: "Script Generation",
  scene_generator: "Scene Generation",
  visual_planner: "Visual Planning",
  audio_planner: "Audio Planning",
  scene_planner: "Scene Elements",
  animation_planner: "Animations",
  render: "Video Render",
};

export const RENDER_STAGE = "render";

export const ALL_PIPELINE_STEPS = [...PROJECT_STAGE_ORDER, RENDER_STAGE] as const;

export interface DisplayScene {
  id: number | string;
  type: string;
  title: string;
  startTime: number;
  endTime: number;
}

interface ContentBlock {
  block_type: string;
  title?: string;
  estimated_duration?: number | string;
  id?: number;
}

function getContentBlocks(
  content: { blocks?: unknown[] } | null | undefined,
): ContentBlock[] {
  if (!content || !Array.isArray(content.blocks)) return [];
  return content.blocks.filter(
    (block): block is ContentBlock =>
      typeof block === "object" &&
      block !== null &&
      "block_type" in block &&
      typeof (block as ContentBlock).block_type === "string",
  );
}

function buildScenesFromBlocks(blocks: ContentBlock[]): DisplayScene[] {
  let currentTime = 0;
  let listItemCount = 0;

  return blocks.map((block, index) => {
    const duration =
      Number(block.estimated_duration) || (block.block_type === "list_item" ? 5 : 4);
    const startTime = currentTime;
    const endTime = currentTime + duration;
    currentTime = endTime;

    let title = block.title?.trim() || "";
    if (block.block_type === "list_item") {
      listItemCount += 1;
      title = title ? `${listItemCount}. ${title}` : `Tip ${listItemCount}`;
    } else if (!title) {
      title = block.block_type
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    }

    return {
      id: block.id ?? `block-${index}`,
      type: block.block_type,
      title,
      startTime,
      endTime,
    };
  });
}

export function getDisplayScenes(project: {
  video_spec?: {
    scenes?: {
      id: number;
      type: string;
      title?: string;
      startTime: number;
      endTime: number;
    }[];
  } | null;
  content?: { blocks?: unknown[] } | null;
}): { scenes: DisplayScene[]; isEstimate: boolean } {
  const specScenes = project.video_spec?.scenes;
  if (specScenes && specScenes.length > 0) {
    return {
      scenes: specScenes.map((scene) => ({
        id: scene.id,
        type: scene.type,
        title: scene.title || "Untitled scene",
        startTime: scene.startTime,
        endTime: scene.endTime,
      })),
      isEstimate: false,
    };
  }

  const blocks = getContentBlocks(project.content);
  if (blocks.length === 0) {
    return { scenes: [], isEstimate: false };
  }

  return {
    scenes: buildScenesFromBlocks(blocks),
    isEstimate: true,
  };
}

export function getPipelineProgress(project: {
  status: string;
  stages_completed?: string[];
  available_stages?: string[];
}) {
  const stagesCompleted = project.stages_completed ?? [];
  const totalSteps = ALL_PIPELINE_STEPS.length;
  const completedStages = stagesCompleted.length;
  const isRendering = project.status === "rendering";
  const isCompleted = project.status === "completed";

  let progressPct = 0;
  if (isCompleted) {
    progressPct = 100;
  } else if (isRendering) {
    progressPct = Math.round(((completedStages + 0.65) / totalSteps) * 100);
  } else if (completedStages > 0) {
    progressPct = Math.round((completedStages / totalSteps) * 100);
  }

  progressPct = Math.min(100, Math.max(0, progressPct));

  const activeStageKey =
    project.status === "rendering"
      ? RENDER_STAGE
      : project.available_stages?.[0] ??
        (completedStages < PROJECT_STAGE_ORDER.length
          ? PROJECT_STAGE_ORDER[completedStages]
          : RENDER_STAGE);

  const activeStepIndex = ALL_PIPELINE_STEPS.indexOf(
    activeStageKey as (typeof ALL_PIPELINE_STEPS)[number],
  );
  const currentStep = activeStepIndex >= 0 ? activeStepIndex + 1 : Math.min(completedStages + 1, totalSteps);
  const currentLabel =
    STAGE_LABELS[activeStageKey] ??
    activeStageKey.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

  return {
    progressPct,
    currentStep,
    totalSteps,
    currentLabel,
    activeStageKey,
    isIndeterminate: progressPct === 0 && !isCompleted,
  };
}

export function getPipelineStepStatus(
  stepKey: string,
  project: {
    status: string;
    stages_completed?: string[];
    available_stages?: string[];
  },
): "done" | "active" | "pending" {
  const stagesCompleted = project.stages_completed ?? [];
  const activeStageKey =
    project.status === "rendering"
      ? RENDER_STAGE
      : project.available_stages?.[0] ?? null;

  if (stepKey === RENDER_STAGE) {
    if (project.status === "completed") return "done";
    if (project.status === "rendering") return "active";
    return "pending";
  }

  if (stagesCompleted.includes(stepKey)) return "done";
  if (activeStageKey === stepKey) return "active";
  return "pending";
}

export const TERMINAL_STATUSES = ["completed", "failed"];

export const STATUS_META: Record<string, { label: string; color: string; bg: string }> = {
  new: { label: "New", color: "#66738a", bg: "rgba(102,115,138,0.12)" },
  script_ready: { label: "Script Ready", color: "#2364e8", bg: "rgba(35,100,232,0.1)" },
  generated: { label: "Scenes Ready", color: "#2364e8", bg: "rgba(35,100,232,0.1)" },
  scenes_ready: { label: "Scenes Ready", color: "#2364e8", bg: "rgba(35,100,232,0.1)" },
  visuals_ready: { label: "Visuals Ready", color: "#1746b3", bg: "rgba(23,70,179,0.1)" },
  elements_ready: { label: "Elements Ready", color: "#1746b3", bg: "rgba(23,70,179,0.1)" },
  audio_ready: { label: "Audio Ready", color: "#1746b3", bg: "rgba(23,70,179,0.1)" },
  animations_ready: { label: "Animations Ready", color: "#1746b3", bg: "rgba(23,70,179,0.1)" },
  rendering: { label: "Rendering", color: "#d97706", bg: "rgba(245,158,11,0.12)" },
  completed: { label: "Completed", color: "#0f9a6b", bg: "rgba(15,154,107,0.12)" },
  failed: { label: "Failed", color: "#dc2626", bg: "rgba(239,68,68,0.1)" },
};

export const getStatusMeta = (status: string) =>
  STATUS_META[status] ?? { label: status, color: "#8b93a7", bg: "rgba(139,147,167,0.15)" };

export const formatDuration = (seconds?: number | null) => {
  if (!seconds && seconds !== 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export const formatDate = (dateStr?: string | null) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const formatShortDate = (dateStr?: string | null) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const formatRelativeTime = (dateStr?: string | null) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";

  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHrs / 24);

  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHrs < 24) return `${diffHrs} hr${diffHrs === 1 ? "" : "s"} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} wk${weeks === 1 ? "" : "s"} ago`;
  }
  return `${diffDays} days ago`;
};

export const formatCardTimestamp = (dateStr?: string | null) => {
  const relative = formatRelativeTime(dateStr);
  const date = formatShortDate(dateStr);
  if (relative && date) return `${relative} · ${date}`;
  return relative || date;
};

export type ProjectTimeSection = "Today" | "Yesterday" | "This Week" | "Older";

export const PROJECT_TIME_SECTIONS: ProjectTimeSection[] = [
  "Today",
  "Yesterday",
  "This Week",
  "Older",
];

export const getProjectTimeSection = (dateStr?: string | null): ProjectTimeSection => {
  if (!dateStr) return "Older";

  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "Older";

  const now = new Date();
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);

  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);

  const weekStart = new Date(todayStart);
  weekStart.setDate(weekStart.getDate() - 7);

  if (date >= todayStart) return "Today";
  if (date >= yesterdayStart) return "Yesterday";
  if (date >= weekStart) return "This Week";
  return "Older";
};

export function groupProjectsByTime<T extends { created_at: string }>(projects: T[]) {
  const groups: Record<ProjectTimeSection, T[]> = {
    Today: [],
    Yesterday: [],
    "This Week": [],
    Older: [],
  };

  const sorted = [...projects].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );

  for (const project of sorted) {
    groups[getProjectTimeSection(project.created_at)].push(project);
  }

  return PROJECT_TIME_SECTIONS.filter((label) => groups[label].length > 0).map((label) => ({
    label,
    count: groups[label].length,
    items: groups[label],
  }));
};
